import {
  selectCampaignReactificationV1Enabled,
  selectDoesUserHaveRbacPermission,
} from 'core/userContext';
import { MessageMedium } from 'modules/graphql/types';
import { TemplateEditViews } from 'modules/templateEditor/common/types';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { useErrorValidationContext } from 'modules/templateEditor/context/ErrorValidationContext';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ActionMenu } from 'ui/actionMenu';
import {
  ActionMenuOption,
  AllowedActionMenuElements,
  MENU_ALIGNMENTS,
  MENU_DIRECTIONS,
} from 'ui/actionMenu/types';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { ChevronDown } from 'ui/icons';
import { LeftTooltip } from 'ui/tooltip';
import noop from 'utils/noop';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { useInStudioTakeoverContext } from '../../context';
import { SetTemplateEditStateFn } from '../../InStudioTemplateEditing.types';

// expected as button & action menu together
const ComboButton = styled.div`
  display: flex;
  > button,
  span[data-test='tooltip-trigger'] > button {
    &:first-child {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
    }
  }
  > *:last-child button {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    border-left-width: 0;
  }
  button,
  span[data-test='tooltip-trigger'] {
    height: 100%;
    + div {
      height: 100%;
      > div[type='button'] {
        height: 100%;
      }
    }
  }
`;

const StyledButton = styled(Button)<{ $hasError: boolean }>`
  &&& {
    border: ${({ theme, $hasError }) =>
      $hasError && `2px solid ${theme.colors.utilityColors.negative100}`};
  }
`;

type ViewOptionsButtonComboProps = {
  setTemplateEditState: SetTemplateEditStateFn;
};

export const ViewOptionsButtonCombo = ({
  setTemplateEditState = noop,
}: ViewOptionsButtonComboProps) => {
  const campaignReactificationV1Enabled = useSelector(selectCampaignReactificationV1Enabled);
  const { campaignId, templateId, view } = useInStudioTakeoverContext();
  const { designContentErrors, designContentWarnings } = useErrorValidationContext();
  const { medium } = useTemplateQueryContext();
  const isEmail = MessageMedium.Email === medium;

  // if user !canDraftMessaging, then only allow previews. No Edit, no Replace, no open to editor tab
  const canDraftMessaging = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.DraftMessaging,
  );

  const OPTIONS = {
    previewWithData: {
      as: AllowedActionMenuElements.Button,
      content: 'Preview with data',
      id: 'btn-preview-with-data',
      onClick: () => setTemplateEditState({ isOpen: true, view: TemplateEditViews.preview }),
    },
    previewOnDevices: {
      as: AllowedActionMenuElements.Link,
      content: 'Preview on devices',
      id: 'btn-preview-on-devices',
      href: `/templates/devicePreview?templateId=${templateId}&view=${view}`,
      target: '_blank',
    },
    openNewTab: {
      as: AllowedActionMenuElements.Link,
      content: 'Open in new tab',
      id: 'btn-new-tab',
      href: campaignReactificationV1Enabled
        ? `/campaigns/${campaignId}?view=Edit&templateEditor=open`
        : `/templates/editor?templateId=${templateId}&campaignId=${campaignId}&view=${view}`,
      target: '_blank',
    },
    replaceContent: {
      as: AllowedActionMenuElements.Button,
      content: 'Replace template',
      id: 'btn-replace-content',
      onClick: () => setTemplateEditState({ isOpen: true, view: TemplateEditViews.select }),
    },
  };

  const viewOptions: ActionMenuOption[] = [
    ...(canDraftMessaging ? [OPTIONS.previewWithData] : []),
    ...(isEmail ? [OPTIONS.previewOnDevices] : []),
    ...(canDraftMessaging ? [OPTIONS.openNewTab] : []),
    ...(canDraftMessaging ? [OPTIONS.replaceContent] : []),
  ];

  const hasDesignError = !!designContentErrors.length || !!designContentWarnings.length;
  return (
    <ComboButton data-test={'view-options-combo'}>
      <LeftTooltip isEnabled={hasDesignError} tooltipContent={'Error in design'}>
        <StyledButton
          data-test={'btn-open-settings'}
          onClick={() => setTemplateEditState({ isOpen: true, view: TemplateEditViews.settings })}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.SECONDARY}
          isDisabled={!canDraftMessaging}
          $hasError={hasDesignError}
        >
          <span>Edit</span>
        </StyledButton>
      </LeftTooltip>
      <ActionMenu
        align={MENU_ALIGNMENTS.RIGHT}
        direction={MENU_DIRECTIONS.BOTTOM}
        options={viewOptions}
        data-test={'view-options-action-menu'}
      >
        <Button size={BUTTON_SIZES.LARGE} variant={BUTTON_VARIANTS.SECONDARY}>
          <ChevronDown height={16} width={16} />
        </Button>
      </ActionMenu>
    </ComboButton>
  );
};

export default ViewOptionsButtonCombo;
